<template>
  <div>
    <h3 class="ui header" style="margin-bottom:0;"><i class="calendar icon"></i>Patient Reporting</h3>
    <h3 class="ui dividing header" style="margin-top:0;"><span class="sub header">This section reports on the completed actions of the patient as well as their Migraine Impact Reports</span></h3>

    <div v-if="inheritedPatient.consent === true">
      <div class="ui primary button"@click.prevent="generateReportsOrCopyLink('en')"
           :class="{ disabled: generatingPDF}">
        <span v-if="language===null || language==='de'"><i class="download icon"></i> Generate Migraine Impact Reports</span>
        <span v-if="language==='en' && generatingPDF===true"><i class="download icon"></i> Generating Migraine Impact Reports</span>
        <span v-if="language==='en' && generatingPDF===false"><i class="download icon"></i> Download Migraine Impact Reports</span>
      </div>

      <div class="ui basic primary button"@click.prevent="generateReportsOrCopyLink('de')"
           :class="{ disabled: generatingPDF}">
        <span v-if="language===null || language==='en'"><i class="download icon"></i> Erzeugen Migräneauswirkungs-Berichte</span>
        <span v-if="language==='de' && generatingPDF===true"><i class="download icon"></i> Vorbereitung Migräneauswirkungs-Berichte</span>
        <span v-if="language==='de' && generatingPDF===false"><i class="download icon"></i> Migräneauswirkungs-Berichte Herunterladen</span>
      </div>

    <br>
    <br>

      <div class="ui styled fluid accordion">

        <div class="title">
          <h4><i class="dropdown icon"></i>
            One-Time Actions
          </h4>
        </div>
        <div class="content">
          <table class="ui very basic unstackable table">
            <thead>
            <tr>
              <th>One Time Tracking (Action Short Names)</th>
              <th>Due Date</th>
              <th class="center aligned">Completion Status</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="item in sortedOneTimeTracking"
                v-if="item !== undefined && item.hasOwnProperty('trackingStatus') && item.trackingStatus.length === 1">
              <td>
                &nbsp; {{ getInheritedPatientActionPlanName(item.id) }}
              </td>
              <td>{{ localizedTimestamp(item.trackingStatus[0].endTimestamp) }}</td>
              <td class="negative center aligned" v-if="item.trackingStatus[0].completionStatus === false">
                <i class="red close icon"></i>
              </td>
              <td class="positive center aligned" v-if="item.trackingStatus[0].completionStatus === true">
                <i class="green checkmark icon"></i>
              </td>
              <td class="center aligned" v-if="item.trackingStatus[0].completionStatus === null">
                <i class="question icon"></i>
              </td>
            </tr>
            </tbody>
          </table>
        </div>


        <div class="active title">
          <h4>
            <i class="dropdown icon"></i>
            Daily Actions & Migraine Status
          </h4>
        </div>

        <div class="active content">
          <table class="ui very basic unstackable table">
            <thead>
            <tr>
              <th class="one wide">Program Day</th>
              <th class="one wide">Migraine Free?</th>
              <th>Daily Tracking<br>(Action Short Names)
                <span class="ui horizontal segment"
                      style="background: #fcfcff; float: right; padding: 0.5em 0.8em; margin-top: -1.5em;">
                  Legend:&nbsp;&nbsp;&nbsp;<span class="ui green compact icon label"><i class="checkmark icon"></i> Completed</span>&nbsp;&nbsp;&nbsp;
                  <span class="ui red compact icon label"><i class="warning icon"></i> Did not complete</span>&nbsp;&nbsp;&nbsp;
                  <span class="ui compact icon label"><i class="question icon"></i> Not filled</span>
                </span>
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(item, index) in initDailyTrackingArray">
              <td class="center aligned">
                &nbsp; {{ index + 1 }}
              </td>
              <td v-if="migraineDaysArray.indexOf(item.timestamp) === -1"
                  class="positive center aligned">
                <i class="green checkmark icon"></i>
              </td>
              <td v-else
                  class="negative center aligned">
                <i  class="red close icon"></i>
              </td>
              <td>
                <div v-for="action in item.trackingStatus"
                     :class="generateLabelColor(action.completionStatus)"
                     style="margin: 0.2em;">
                  <i style="margin-right: 0.5em" :class="generateTrackingIcon(action.completionStatus)"></i>
                  <span style="margin-right: 0.5em; opacity: 0.8">{{ action.id }}</span>
                  {{ getInheritedPatientActionPlanName(action.id) }}
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div v-else>
      No reporting functions available as patient has yet to provide consent.
    </div>

    <modal-generate-p-d-f
      @modalId="setModalId"
      @errorModalId="setErrorModalId"
      @retryGenerateMIR="generateMIR"
      :generatingPDF="generatingPDF"
      :pdfShortLink="pdfShortLink"
      :pdfLongLink="pdfLongLink"
      :language="language"/>
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  import ModalGeneratePDF from './ModalGeneratePDF'

	export default {
    props: {
      inheritedPatient: {
        type: Object,
        required: true
      },
      patientProfilePlan: {
        type: Object,
        required: true
      }
    },
    components: {
      ModalGeneratePDF
    },
		data() {
			return {
			  migraineDaysArray: [],
        generatingPDF: null,
        pdfShortLink: null,
        pdfLongLink: null,
        language: null,
        modalId: null,
        errorModalId: null,
        timeout: 20000
      }
		},
		computed: {
      ...mapState(['userAuthToken','userId']),

      sortedOneTimeTracking: function() {

        // TODO: Implement sorting on the one-time tracking on the due date. dynamicNestedSort does not work
        let coachVersionOneTimeTracking = this.patientProfilePlan.data.oneTimeTracking;
        let patientVersionOneTimeTracking = this.inheritedPatient.data.hasOwnProperty('oneTimeTracking') ? this.inheritedPatient.data.oneTimeTracking : this.patientProfilePlan.data.oneTimeTracking;

        // Create a deep copy to render the Patient Reporting table to avoid mutating passed-in prop `patientProfilePlan.data.oneTimeTracking`
        let oneTimeTrackingArray = JSON.parse(JSON.stringify(coachVersionOneTimeTracking));

        for (let i = 0; i < coachVersionOneTimeTracking.length; i++ ) {
          // Look for the tracker in `patientVersionOneTimeTracking`
          let updatedOneTimeAction = patientVersionOneTimeTracking.find((oneTimeAction)=> {
            if (oneTimeAction !== null) {
              return parseInt(oneTimeAction.id) === parseInt(coachVersionOneTimeTracking[i].id)
            }
          });

          /*
          - If `patientVersionOneTimeTracking` does not have the tracker, `updatedOneTimeAction` will be `undefined`
          - If `updatedOneTimeAction` is `undefined`, then default to `coachVersionOneTimeTracking`
          - Otherwise, use tracker in `patientVersionOneTimeTracking` and push to `oneTimeTrackingArray`
          */
          if (updatedOneTimeAction !== null && typeof(updatedOneTimeAction) !== 'undefined') {
            oneTimeTrackingArray[i] = updatedOneTimeAction;
          }
        }

        return oneTimeTrackingArray;
      },
      reportTimePeriod: function() {
        let startTime = this.patientProfilePlan.data.startDate;
        let endTime = this.convertLocaltoUTCTimeStamp(new Date());

        return {
          startTime: startTime,
          endTime: endTime
        }
      },
      reportRequestURL: function() {
        return process.env.API_URL + process.env.PROGRAM_ID + '/patients/' + this.inheritedPatient.userId + '/migraineDays?startTime=' + this.reportTimePeriod.startTime + '&endTime=' + this.reportTimePeriod.endTime
      },
      initDailyTrackingArray: function() {
        let coachVersionDailyTracking = this.patientProfilePlan.data.dailyTracking;
        let patientVersionDailyTracking = this.inheritedPatient.data.hasOwnProperty('dailyTracking') ? this.inheritedPatient.data.dailyTracking : this.patientProfilePlan.data.dailyTracking;

        let timestampTrackingArray = [];

        // FIXME: Maybe this should be set in the store for consistency??? ¯\_(ツ)_/¯
        let currentUTCDateTimestamp = this.convertLocaltoUTCTimeStamp(new Date());
        let patientCurrentCoachingDay = Math.floor( (currentUTCDateTimestamp - this.patientProfilePlan.data.startDate ) / 86400000 ) + 1;

        // Initialize an array of objects with key timestamp if the patient has started the coaching program
        if (patientCurrentCoachingDay > 0 ) {
          for (let i = 0; i < patientCurrentCoachingDay; i++) {
            timestampTrackingArray.push({ timestamp: this.patientProfilePlan.data.startDate + (86400000 * i) });
          }
        }

        if (timestampTrackingArray.length !== 0) {

          // Loop through `dailyTracking` key to see if any action has a timestamp for the corresponding day
          timestampTrackingArray.forEach((programDay) => {
            programDay.trackingStatus = [];
            coachVersionDailyTracking.forEach((action) => {
              let updatedAction = patientVersionDailyTracking.find((updatedAction) => updatedAction.id === action.id);

              /*
              - REF: See comments in `sortedOneTimeTracking()`
              - If patient has not opened up Employer Buddy, most updated action is `coachVersionDailyTracking`.
              */
              updatedAction = typeof(updatedAction) !== 'undefined' ? updatedAction : action;

              let updatedDailyTracking = updatedAction.trackingStatus.find((dailyTracking) => {
                return dailyTracking.startTimestamp === programDay.timestamp
              });

              if (typeof(updatedDailyTracking) !== 'undefined') {
                programDay.trackingStatus.push({
                  id: action.id,
                  completionStatus: updatedDailyTracking.completionStatus
                });
              }
            });
          });
        }

        return timestampTrackingArray;
      }

    },
		methods: {
      getMigraineDays() {
        this.$http.get(this.reportRequestURL).then((response)=>{
          let allDailyEvents = response.body.result.dailyEventsData;
          let allMigDays = [];

          if (allDailyEvents.hasOwnProperty('mig')) {
            Object.keys(allDailyEvents.mig).forEach((subtype)=> {
              Object.keys(allDailyEvents.mig[subtype]).forEach((month)=>{
                allDailyEvents.mig[subtype][month].forEach((UTCtimestamp)=> {
                  allMigDays.push(UTCtimestamp);
                });
              })
            })
          }
          this.migraineDaysArray =  allMigDays;
        }, (error) => {
          console.log(error);
          if (error.status !== 401) {
            Rollbar.error("Error in fetching Migraine Days", error);
          }
        })
      },
      getInheritedPatientActionPlanName(actionid) {
        let action = this.inheritedPatient.userPlans.find((element) => {
          return element.data.hasOwnProperty('id') && element.data.id.toString() === actionid.toString();
        });

        if (typeof(action)!== 'undefined' && typeof(action.data)!== 'undefined' && typeof(action.data.shortName) !== 'undefined') {
          return action.data.shortName
        }
        else {
          Rollbar.warning('No matching Action Short Name for Patient ID: ' + this.inheritedPatient.id + 'and Action ID: ' + actionid);
          return 'No Short Name provided'
        }
      },
      generateLabelColor(val) {
        switch (val) {
          case true:
            return 'ui green label';
          case false:
            return 'ui red label';
          default:
            return 'ui label'
        }
      },
      generateTrackingIcon(val) {
        switch (val) {
          case true:
            return 'checkmark icon';
          case false:
            return 'warning icon';
          case null:
            return 'question icon';
          default:
            return 'question icon';
        }
      },
      generateReportsOrCopyLink: function(language) {
        if (this.language === language) {
          if (this.generatingPDF === null) {
            this.generateMIR();
          } else if (this.generatingPDF === false) {
            this.showModal();
          }
        } else if (this.language !== language) {
          this.language = language;
          this.resetPDFGeneration();
          this.generateMIR();
        }
      },
      resetPDFGeneration: function() {
        this.pdfShortLink = null;
        this.pdfLongLink = null;
        this.generatingPDF = null;
      },
      setModalId: function(modalId) {
        this.modalId = modalId;
      },
      setErrorModalId: function(errorModalId) {
        this.errorModalId = errorModalId;
      },
      showModal: function() {
        $('#' + this.modalId)
          .modal({ closable: false, autofocus: false, observeChanges: true, transition: 'fade' })
          .modal('show');
      },
      showErrorModal: function() {
        $('#' + this.errorModalId)
          .modal({ closable: false, autofocus: false, observeChanges: true, transition: 'fade' })
          .modal('show');
      },
      hideModal: function() {
        $('#' + this.modalId).modal('hide');
      },
      generateMIR() {
        return new Promise((resolve, reject) => {
          this.generatingPDF = true;
          this.showModal();

          let requestUrl = process.env.API_URL + process.env.PROGRAM_ID + '/hcp-pdf/' + this.inheritedPatient.userId + '?waitForElement=false';

          let now = this.convertLocaltoUTCTimeStamp(new Date());
          let programEndDate = this.patientProfilePlan.data.endDate;
          let startTime = this.patientProfilePlan.data.startDate;
          let endTime;

          switch (programEndDate) {
            case null:
              endTime = now;
              break;
            default:
              /*
              - If date now is smaller than or equal to program end date, then use date now
              - If date now is bigger than program end date, use program end date
              */
              endTime = now <= programEndDate ? now : programEndDate;
              break;
          }

          let country = 'CH';

          let reportURL = process.env.PRINT_URL + '?userAuthToken=' + encodeURIComponent(this.userAuthToken) + '&uid=' + this.inheritedPatient.userId + '&startTime=' + startTime + '&endTime=' + endTime + '&country=' + country + '&lang=' + this.language + '&mode=coach#/print';

          this.$http.get(requestUrl, {
            headers:
              {
                'Content-type': 'application/json',
                'accept': 'application/json',
                'x-auth-token': this.userAuthToken,
                'report-url': reportURL // FIXME: This will be the production MIR link of Migraine Impact Reports
              },
            timeout: this.timeout
          }).then((response) => {
            this.pdfLongLink = response.body.result.longUrl;
            this.pdfShortLink = response.body.result.shortUrl;
            this.generatingPDF = false;

            resolve();
          }, (error) => {
            setTimeout(() => {
              this.hideModal();
            }, 1000);
            setTimeout(() => {
              this.resetPDFGeneration();
            }, 2000);
            setTimeout(() => {
              Rollbar.error(`Failed to generate MIR for \`userId\`: ${this.inheritedPatient.userId}`, error);
              this.showErrorModal();
              reject(error);
            }, 2000);
          });
        }).catch((reason) => {
          Rollbar.error(`Request to generate MIR for \`userId\` hit ${this.timeout} milliseconds timeout`, reason)
        });
      }
    },
		mounted() {
        this.getMigraineDays();

        $('.ui .accordion').accordion();
		}
	}
</script>

<style>

</style>



// WEBPACK FOOTER //
// src/components/patients/PatientReports.vue